import apiClient from './apiClient';
import type {
  GoalNode,
  GoalConflictResolution,
  GoalImportRow,
  GoalImportSummary,
} from '@upskill/shared';

// ============================================================================
// CRUD payload types
// ============================================================================

export interface CreateGoalDto {
  level: 'DOMAIN' | 'PROGRAM' | 'GOAL_TARGET';
  parentGoalId?: string;
  domain?: string;
  program?: string;
  name: string;
  status?: string;
  shortTermNote?: string;
  dateInitiated?: string;
  dateMet?: string;
}

export type UpdateGoalDto = Partial<
  Pick<CreateGoalDto, 'name' | 'status' | 'shortTermNote' | 'dateInitiated' | 'dateMet'>
> & {
  /** Recorded on GoalStatusHistory.note when this update changes `status`. */
  note?: string;
};

// ============================================================================
// Import (validate -> preview -> resolve -> confirm) types
// ============================================================================

export interface GoalImportValidateResponse {
  rows: GoalImportRow[];
  summary: GoalImportSummary;
  unmatched: GoalImportRow[];
  conflicts: GoalImportRow[];
}

export interface GoalImportResolutionInput {
  rowNumber: number;
  resolution: GoalConflictResolution;
}

export interface GoalImportExecutePayload {
  resolutions: GoalImportResolutionInput[];
  clientId?: string;
}

export interface GoalImportExecuteResponse {
  new: number;
  updated: number;
  reintroduced: number;
  unchanged: number;
  unmatched: number;
  errors: Array<{ rowNumber: number; reason: string }>;
}

const goalService = {
  /** Picker source — flat, pickable-leaf-only list (Red-Dot dropdown, unchanged shape). */
  getGoalsForClient: async (clientId: string): Promise<any[]> => {
    const res = await apiClient.get(`/api/v1/clients/${clientId}/goals`, {
      params: { pickableOnly: true },
    });
    return res.data?.data || res.data || [];
  },

  /** Full Domain -> Program -> Goal/Target tree for the Goals management page. */
  async getGoalTree(clientId: string): Promise<GoalNode[]> {
    const { data } = await apiClient.get(`/api/v1/clients/${clientId}/goals`, {
      params: { tree: true },
    });
    return data?.data ?? data ?? [];
  },

  async createGoal(clientId: string, dto: CreateGoalDto): Promise<GoalNode> {
    const { data } = await apiClient.post(`/api/v1/clients/${clientId}/goals`, dto);
    return data?.data ?? data;
  },

  async updateGoal(goalId: string, dto: UpdateGoalDto): Promise<GoalNode> {
    const { data } = await apiClient.patch(`/api/v1/goals/${goalId}`, dto);
    return data?.data ?? data;
  },

  async deleteGoal(goalId: string): Promise<void> {
    await apiClient.delete(`/api/v1/goals/${goalId}`);
  },

  /**
   * Dry-run validate an import CSV (5-gate engine). Nothing is written until
   * `importExecute`. `clientId` scopes a single-client import; omit for the
   * onboarding bulk (multi-client, fan-out by `client_external_id`) import.
   */
  async importValidate(file: File, clientId?: string): Promise<GoalImportValidateResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (clientId) formData.append('clientId', clientId);
    const { data } = await apiClient.post('/api/v1/goals/import/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data?.data ?? data;
  },

  async importExecute(payload: GoalImportExecutePayload): Promise<GoalImportExecuteResponse> {
    const { data } = await apiClient.post('/api/v1/goals/import/execute', payload);
    return data?.data ?? data;
  },

  /** Downloads the "export clients (with IDs)" round-trip-key CSV as a blob. */
  async exportClients(): Promise<Blob> {
    const { data } = await apiClient.get('/api/v1/clients/export', {
      params: { format: 'csv' },
      responseType: 'blob',
    });
    return data;
  },
};

export default goalService;
